import React, { useState } from "react";
import { Collapse } from "react-bootstrap";

import { EditOrderData } from "@admin/domain/orders/dto/EditOrderData";
import SimpleOrder from "../SimpleOrder";
import { useOrderContext } from "@admin/pages/OrderEditor/OrderContext";
import AccountingAndAnaliticalDb from "../../AccountingAndAnaliticalDb";
import formatMoney from "@admin/domain/orders/formatMoney";

export default function ConsolidateOrder() {
    const { order, updateOrder } = useOrderContext();

    const [collapseState, setCollapseState] = React.useState<Map<number, boolean>>(
        new Map(order.sub_orders.map((order: EditOrderData) => [order.id, false])),
    );

    const toggleCollapse = (order: EditOrderData): void => {
        setCollapseState(new Map([...collapseState, [order.id, !collapseState.get(order.id)]]));
    };

    const [hover, setHover] = useState(false);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(order.delivery_cost / 100);
    const [orderAmount] = useState(order.total_amount - order.delivery_cost);

    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (e.key === `Enter`) {
            try {
                const response = await fetch(`/api/v1/orders/${order.id}/change-delivery-cost`, {
                    method: `POST`,
                    headers: {
                        "Content-Type": `application/json`,
                    },
                    body: JSON.stringify({ delivery_cost: value }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`Ошибка сохранения`);
                }

                updateOrder({
                    delivery_cost: value * 100,
                    total_amount: orderAmount + data.payload.shipping_info.delivery_cost,
                });
                setEditing(false);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
            }
        }
    };

    const [isPayable, setIsPayable] = useState(order.is_payable ?? false);
    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIsPayable(checked);

        try {
            await fetch(`/api/v1/orders/${order.id}/set-order-payable`, {
                method: `POST`,
                headers: {
                    "Content-Type": `application/json`,
                },
                body: JSON.stringify({ is_payable: checked }),
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Ошибка при обновлении:`, err);
        }
    };

    return (
        <div>
            {order.sub_orders.map((subOrder: EditOrderData) => (
                <div key={subOrder.id} className="border rounded p-5 mb-3">
                    <div
                        className={`cursor-pointer ${collapseState.get(subOrder.id) ? `mb-5` : ``}`}
                        onClick={(): void => toggleCollapse(subOrder)}
                    >
                        <a href={`/admin/orders/${subOrder.id}`} target="_blank">
                            Подзаказ № {subOrder.id} {formatMoney(subOrder, `total_amount`)}
                        </a>
                    </div>
                    <Collapse in={collapseState.get(subOrder.id)}>
                        <div>
                            <SimpleOrder order={subOrder} />
                            <AccountingAndAnaliticalDb order={subOrder} />
                        </div>
                    </Collapse>
                </div>
            ))}

            <div className="mb-5">
                <span className="fw-bolder">Доставка: </span>

                {editing ? (
                    <input
                        type="number"
                        value={value}
                        autoFocus
                        onChange={(e) => setValue(Number(e.target.value))}
                        onKeyDown={handleKeyDown}
                        onBlur={() => setEditing(false)} // закрыть если ушли фокусом
                        style={{ padding: `2px 4px`, borderRadius: `4px` }}
                    />
                ) : (
                    <span
                        style={{
                            backgroundColor: hover ? `#f0f0f0` : `transparent`,
                            cursor: `pointer`,
                            borderRadius: `4px`,
                            padding: `2px 4px`,
                        }}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        onClick={() => setEditing(true)}
                    >
                        {order.delivery_cost > 0 ? formatMoney(order, `delivery_cost`) : `бесплатно`}
                    </span>
                )}
            </div>

            <div className="mb-5">
                <span className="fw-bolder">Итоговая стоимость: </span>
                <span>{formatMoney(order, `total_amount`)}</span>
            </div>

            <div className="mb-5">
                <span className="fw-bolder">Баллы: </span>
                <span>
                    {order.sub_orders.reduce(
                        (total: number, order: EditOrderData): number => total + order.total_points,
                        0,
                    )/100}
                </span>
            </div>

            <div className="mb-5">
                <label className="flex items-center space-x-2">
                    <input type="checkbox" checked={isPayable} onChange={handleChange} className="form-checkbox" />
                    <span>Разрешить оплату</span>
                </label>
            </div>
        </div>
    );
}
